import { logger } from "./logger";
// Analytics utility for Google Analytics and Google Tag Manager
// Handles dynamic script loading and event tracking

export interface AnalyticsConfig {
  enabled: boolean;
  googleAnalyticsId?: string;
  googleTagManagerId?: string;
  cookieConsentRequired: boolean;
}

export interface AnalyticsEvent {
  action: string;
  category: string;
  label?: string;
  value?: number;
}

class AnalyticsService {
  private config: AnalyticsConfig | null = null;
  private initialized = false;
  private consentGiven = false;
  private pendingEvents: AnalyticsEvent[] = [];

  /**
   * Initialize analytics with configuration from backend
   */
  async initialize(config: AnalyticsConfig): Promise<void> {
    this.config = config;

    if (!config.enabled) {
      logger.log("[Analytics] Analytics disabled in configuration");
      return;
    }

    // Check if cookie consent is required
    if (config.cookieConsentRequired) {
      const consent = this.getCookieConsent();
      if (!consent) {
        logger.log("[Analytics] Waiting for cookie consent");
        return;
      }
    }

    await this.loadScripts();
    this.initialized = true;
    this.consentGiven = true;

    // Process any pending events
    this.processPendingEvents();
  }

  /**
   * Load Google Analytics and Google Tag Manager scripts
   */
  private async loadScripts(): Promise<void> {
    const promises: Promise<void>[] = [];

    // Load Google Analytics
    if (this.config?.googleAnalyticsId) {
      promises.push(this.loadGoogleAnalytics(this.config.googleAnalyticsId));
    }

    // Load Google Tag Manager
    if (this.config?.googleTagManagerId) {
      promises.push(this.loadGoogleTagManager(this.config.googleTagManagerId));
    }

    await Promise.all(promises);
  }

  /**
   * Load Google Analytics script
   */
  private loadGoogleAnalytics(measurementId: string): Promise<void> {
    return new Promise((resolve, reject) => {
      // Check if already loaded
      if (
        document.querySelector(`script[src*="googletagmanager.com/gtag/js"]`)
      ) {
        logger.log("[Analytics] Google Analytics already loaded");
        resolve();
        return;
      }

      // Load gtag.js script
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.onload = () => {
        // Initialize gtag
        window.dataLayer = window.dataLayer || [];
        function gtag(...args: unknown[]) {
          window.dataLayer.push(args);
        }
        window.gtag = gtag;

        gtag("js", new Date());
        gtag("config", measurementId, {
          send_page_view: false, // We'll handle page views manually
        });

        logger.log("[Analytics] Google Analytics loaded:", measurementId);
        resolve();
      };
      script.onerror = () => {
        logger.error("[Analytics] Failed to load Google Analytics");
        reject(new Error("Failed to load Google Analytics"));
      };

      document.head.appendChild(script);
    });
  }

  /**
   * Load Google Tag Manager script
   */
  private loadGoogleTagManager(containerId: string): Promise<void> {
    return new Promise((resolve) => {
      // Check if already loaded
      if (
        document.querySelector(`script[src*="googletagmanager.com/gtm.js"]`)
      ) {
        logger.log("[Analytics] Google Tag Manager already loaded");
        resolve();
        return;
      }

      // Initialize dataLayer
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        "gtm.start": new Date().getTime(),
        event: "gtm.js",
      });

      // Load GTM script
      const script = document.createElement("script");
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`;
      script.onload = () => {
        logger.log("[Analytics] Google Tag Manager loaded:", containerId);
        resolve();
      };
      script.onerror = () => {
        logger.error("[Analytics] Failed to load Google Tag Manager");
        resolve(); // Don't reject, just continue
      };

      document.head.appendChild(script);

      // Add noscript iframe for GTM
      const noscript = document.createElement("noscript");
      const iframe = document.createElement("iframe");
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${containerId}`;
      iframe.height = "0";
      iframe.width = "0";
      iframe.style.display = "none";
      iframe.style.visibility = "hidden";
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
    });
  }

  /**
   * Track page view
   */
  trackPageView(path: string, title?: string): void {
    if (!this.isReady()) {
      logger.log("[Analytics] Not ready, skipping page view:", path);
      return;
    }

    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_path: path,
        page_title: title || document.title,
      });
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: "page_view",
        page_path: path,
        page_title: title || document.title,
      });
    }

    logger.log("[Analytics] Page view tracked:", path);
  }

  /**
   * Track custom event
   */
  trackEvent(event: AnalyticsEvent): void {
    if (!this.isReady()) {
      // Queue event if consent is required but not given yet
      if (this.config?.cookieConsentRequired && !this.consentGiven) {
        this.pendingEvents.push(event);
        logger.log("[Analytics] Event queued (waiting for consent):", event);
        return;
      }
      logger.log("[Analytics] Not ready, skipping event:", event);
      return;
    }

    if (window.gtag) {
      window.gtag("event", event.action, {
        event_category: event.category,
        event_label: event.label,
        value: event.value,
      });
    }

    if (window.dataLayer) {
      window.dataLayer.push({
        event: event.action,
        event_category: event.category,
        event_label: event.label,
        event_value: event.value,
      });
    }

    logger.log("[Analytics] Event tracked:", event);
  }

  /**
   * Grant cookie consent and initialize analytics
   */
  async grantConsent(): Promise<void> {
    this.setCookieConsent(true);
    this.consentGiven = true;

    if (this.config && !this.initialized) {
      await this.loadScripts();
      this.initialized = true;
      this.processPendingEvents();
    }
  }

  /**
   * Revoke cookie consent
   */
  revokeConsent(): void {
    this.setCookieConsent(false);
    this.consentGiven = false;
    this.initialized = false;
    // Note: Scripts remain loaded but tracking stops
  }

  /**
   * Check if analytics is ready to track
   */
  private isReady(): boolean {
    return (
      this.initialized && this.consentGiven && (this.config?.enabled ?? false)
    );
  }

  /**
   * Process pending events after consent is granted
   */
  private processPendingEvents(): void {
    if (this.pendingEvents.length > 0) {
      logger.log(
        `[Analytics] Processing ${this.pendingEvents.length} pending events`,
      );
      this.pendingEvents.forEach((event) => this.trackEvent(event));
      this.pendingEvents = [];
    }
  }

  /**
   * Get cookie consent status from localStorage
   */
  private getCookieConsent(): boolean {
    try {
      return localStorage.getItem("analytics_consent") === "true";
    } catch {
      return false;
    }
  }

  /**
   * Set cookie consent status in localStorage
   */
  private setCookieConsent(consent: boolean): void {
    try {
      localStorage.setItem("analytics_consent", consent.toString());
    } catch (error) {
      logger.error("[Analytics] Failed to save consent:", error);
    }
  }

  /**
   * Check if consent has been given
   */
  hasConsent(): boolean {
    return this.consentGiven || !this.config?.cookieConsentRequired;
  }

  /**
   * Check if consent is required
   */
  isConsentRequired(): boolean {
    return this.config?.cookieConsentRequired ?? false;
  }

  /**
   * Get current configuration
   */
  getConfig(): AnalyticsConfig | null {
    return this.config;
  }
}

// Global analytics instance
export const analytics = new AnalyticsService();

// Extend Window interface for TypeScript
declare global {
  interface Window {
    dataLayer: unknown[];
    gtag: (...args: unknown[]) => void;
  }
}
